import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import moment, { Moment } from "moment";
import { css } from "@emotion/react";
import {
    DateValidationError,
    PickerChangeHandlerContext
} from "@mui/x-date-pickers";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import { EditInlineDialogProps } from "components/Actions/dialogs/types/interfaces";
import DatePicker, { useDatePicker } from "components/DatePicker";
import Autocomplete from "components/DesignComponents/Autocomplete";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import { disableDates, isInternalTypeCompany } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { maxDialogWidth } from "shared/styles/styles";
import { DROPDOWN_SIZES, ENDPOINTS } from "shared/types/enums";

import { hasImeiRestriction } from "../../../shared/functions";
import { CREATE_VALUE_NAMES } from "../../CreateDesktopLicense/types/enums";
import { useLicenseValidation } from "../../hooks/useLicenseValidation";
import { EDIT_VALUE_NAMES, STATUS, Value } from "./types";

const EditDesktopLicense = ({ data, isOpen, close }: EditInlineDialogProps) => {
    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();
    const { parseDatePickerValue } = useDatePicker();
    const { handleResponse, updateData } = useApi();

    const { rootCompanyType } = useContext(ParamsContext);
    const { isSuperUser } = useContext(AuthContext);
    const isInternalType = isInternalTypeCompany(rootCompanyType);

    const [isLoading, setIsLoading] = useState(false);
    const [hasExpiresAtError, setHasExpiresAtError] = useState(false);

    const hasImeiRestrictionInputField = hasImeiRestriction(
        isInternalType,
        isSuperUser
    );

    const [value, setValue] = useState<Value>({
        [EDIT_VALUE_NAMES.Name]: "",
        [EDIT_VALUE_NAMES.Enabled]: null,
        [EDIT_VALUE_NAMES.ImeiRestricted]: null,
        [EDIT_VALUE_NAMES.ExpiresAt]: null,
        [EDIT_VALUE_NAMES.ClientIp]: ""
    });

    const { name, enabled, imei_restricted, expires_at, client_ip } = value;

    const {
        nameError,
        ipv4Error,
        MAX_EXPIRATION_YEARS,
        isValidIpv4,
        validateHardwareKeyOrIpv4,
        resetError
    } = useLicenseValidation(name);

    useEffect(() => {
        if (data) {
            const { name, enabled, imei_restricted, expires_at, client_ip } =
                data;

            setValue({
                [EDIT_VALUE_NAMES.Name]: name,
                [EDIT_VALUE_NAMES.Enabled]: enabled,
                [EDIT_VALUE_NAMES.ImeiRestricted]: imei_restricted,
                [EDIT_VALUE_NAMES.ExpiresAt]: expires_at,
                [EDIT_VALUE_NAMES.ClientIp]: client_ip
            });
        }
    }, [data]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const newValue = e.target.value;
        const isIpInput = name === EDIT_VALUE_NAMES.ClientIp;

        resetError(false, isIpInput);

        setValue(prevValue => ({
            ...prevValue,
            [name]: newValue
        }));
    };

    const handleExpiresAtChange = (
        dateValue: Moment | null,
        context: PickerChangeHandlerContext<DateValidationError>
    ) => {
        const newValue = parseDatePickerValue(dateValue, context, expires_at);

        setValue(prevValue => ({
            ...prevValue,
            [EDIT_VALUE_NAMES.ExpiresAt]: newValue
        }));
    };

    const handleImeiRestrictionDropdownChange = (newValue: boolean) =>
        setValue(prevValue => ({
            ...prevValue,
            [EDIT_VALUE_NAMES.ImeiRestricted]: newValue
        }));

    const handleExpiresAtError = (error: string | null) =>
        setHasExpiresAtError(error !== null);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const payload = {
                name,
                enabled,
                expires_at,
                client_ip: client_ip || null,
                ...(hasImeiRestrictionInputField ? { imei_restricted } : {})
            };

            const response = await updateData(
                ENDPOINTS.DesktopLicenses,
                data.id,
                payload
            );

            close();
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }
        setIsLoading(false);
    };

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator
            title={t("General##edit desktop license")}
            open={isOpen}
            close={close}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setValue({
                            [EDIT_VALUE_NAMES.Name]: "",
                            [EDIT_VALUE_NAMES.Enabled]: null,
                            [EDIT_VALUE_NAMES.ExpiresAt]: null,
                            [EDIT_VALUE_NAMES.ClientIp]: "",
                            [EDIT_VALUE_NAMES.ImeiRestricted]: null
                        });
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={
                        name.trim() &&
                        !nameError &&
                        enabled !== null &&
                        (!hasImeiRestrictionInputField ||
                            imei_restricted !== null) &&
                        expires_at !== null &&
                        !hasExpiresAtError &&
                        isValidIpv4(client_ip || "")
                    }
                />
            }
            css={css(fromMd && maxDialogWidth)}
            extendToMaxWidth={toMd}
        >
            <div
                css={css({
                    "& > div + div": {
                        marginTop: "16px"
                    },

                    marginBottom: "16px"
                })}
            >
                <InputField
                    fullWidth
                    required
                    size={INPUT_FIELD_SIZES.Medium}
                    name={EDIT_VALUE_NAMES.Name}
                    value={name}
                    readOnly={isLoading}
                    labelLeft={t("Dialog##name")}
                    placeholder={t("Dialog##enter")}
                    onChange={handleInputChange}
                    errorText={nameError}
                />

                <Autocomplete
                    labelLeft={t("Table##status")}
                    textFieldParams={{ placeholder: t("Dialog##select") }}
                    fullWidth
                    disableClearable
                    size={DROPDOWN_SIZES.Medium}
                    options={Object.values(STATUS)}
                    value={enabled ? STATUS.Enabled : STATUS.Disabled}
                    loading={isLoading}
                    readOnly={isLoading}
                    getOptionLabel={option => t(`Table##license ${option}`)}
                    onChange={(_, val: STATUS) =>
                        setValue(prevValue => ({
                            ...prevValue,
                            [EDIT_VALUE_NAMES.Enabled]: val === STATUS.Enabled
                        }))
                    }
                />

                {hasImeiRestrictionInputField && (
                    <Autocomplete
                        labelLeft={t("Dialog##imei restriction")}
                        textFieldParams={{ placeholder: t("Dialog##select") }}
                        fullWidth
                        disableClearable
                        readOnly={isLoading}
                        size={DROPDOWN_SIZES.Medium}
                        options={[false, true]}
                        value={imei_restricted}
                        getOptionLabel={option =>
                            t(
                                `General##${option ? "restricted" : "unrestricted"}`
                            )
                        }
                        onChange={(_, val) =>
                            handleImeiRestrictionDropdownChange(val)
                        }
                    />
                )}

                <DatePicker
                    label={t("Dialog##expires at")}
                    disablePast
                    readOnly={isLoading}
                    value={expires_at ? moment.utc(expires_at) : null}
                    shouldDisableYear={date =>
                        disableDates(date, MAX_EXPIRATION_YEARS, "years")
                    }
                    shouldDisableMonth={date =>
                        disableDates(date, MAX_EXPIRATION_YEARS, "years")
                    }
                    shouldDisableDate={date =>
                        disableDates(date, MAX_EXPIRATION_YEARS, "years")
                    }
                    onChange={handleExpiresAtChange}
                    getError={handleExpiresAtError}
                    textFieldParams={{
                        required: true
                    }}
                />

                <InputField
                    fullWidth
                    size={INPUT_FIELD_SIZES.Medium}
                    name={EDIT_VALUE_NAMES.ClientIp}
                    value={client_ip || ""}
                    readOnly={isLoading}
                    labelLeft={`${t("General##client ip")} (${t("Dialog##optional")})`}
                    placeholder={t("Dialog##enter")}
                    onChange={handleInputChange}
                    onBlur={() =>
                        validateHardwareKeyOrIpv4(
                            CREATE_VALUE_NAMES.ClientIp,
                            client_ip || ""
                        )
                    }
                    errorText={ipv4Error}
                />
            </div>
        </Dialog>
    );
};

export default EditDesktopLicense;
